


























































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import CategoryBarRow from "./CategoryBarRow.vue";
import CountColumnHeader from "./CountColumnHeader.vue";
import GenAiCard from "./GenAiCard.vue";
import GenAiDonut from "./GenAiDonut.vue";
import LevelSlider from "./LevelSlider.vue";
import ViewModeToggle from "./ViewModeToggle.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { LOAD_FAILED_TEXT, state } from "../data/state";
import {
  CategoryCount,
  LevelBreakdown,
  SEVERITY_STOPS,
  by_category,
  describe_levels,
} from "../data/model";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";
import { show_gen_ai_category_events } from "../data/drilldown";
import { risk_share_line } from "../data/share";
import { use_view_mode } from "../data/view-mode";

const TONE_LABEL: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** the drill-down speaks the app's numeric severity encoding */
const LEVEL_BY_TONE: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/**
 * Widget 1 — "Risky Employees' Activities" (gen-ai-ui, Sep 2026).
 *
 * Employee -> AI only: information employees shared or uploaded to AI. The
 * AI -> content side lives in RiskBySeverityCard; the prototype is explicit
 * that the two "are not the same" and must not be summed.
 *
 * Three slider stops, not four. This is a RISK slider and "none" is the
 * absence of risk — the prototype drops it rather than offering "All" as a
 * fourth level that silently mixes in non-risky activity.
 */
export default Vue.extend({
  components: {
    CategoryBarRow,
    CountColumnHeader,
    GenAiCard,
    GenAiDonut,
    LevelSlider,
    ViewModeToggle,
    SkeletonLoaderItem,
  },
  setup() {
    const palette = useGenAiPalette();
    const view = use_view_mode("riskByCategory");

    const items = computed(() =>
      by_category(state.overview.value.risk_cube, "employee")
    );
    const is_loading = computed(() => state.loading.value.is_loading);

    /** opens on the widest stop, so the card shows the full picture first */
    const level = ref(3);
    const active_keys = computed(() =>
      SEVERITY_STOPS.slice(0, level.value)
    );

    const segment = (item: CategoryCount, key: keyof LevelBreakdown) =>
      (item.severities && item.severities[key]) || 0;

    const total_for = (item: CategoryCount) =>
      active_keys.value.reduce((sum, key) => sum + segment(item, key), 0);

    const headline = computed(() =>
      items.value.reduce((sum, item) => sum + total_for(item), 0)
    );

    /** scale every bar against the widest row at the CURRENT stop, so narrowing
     *  the slider re-scales instead of leaving every bar stumpy */
    const max_value = computed(() =>
      Math.max(1, ...items.value.map((d) => total_for(d)))
    );

    const segments_for = (item: CategoryCount) =>
      active_keys.value.map((key) => ({
        key,
        value: segment(item, key),
        color: palette.value.severity[key].bar,
        title: `${TONE_LABEL[key]}: ${segment(item, key)} activities`,
      }));

    /** a failed request has no count: a dash, never a zero */
    const load_failed = computed(() => state.failed.value.employees_risk);

    const subtitle = computed(() =>
      risk_share_line(
        state.overview.value.shares,
        "employee",
        headline.value,
        active_keys.value
      )
    );

    const level_text = computed(
      () => active_keys.value.map((k) => TONE_LABEL[k]).join(", ") + " severity"
    );

    /** the label has to say what the click will do, and carry the split the
     *  stacked bar shows only in colour */
    const aria_for = (item: CategoryCount) =>
      `Open ${total_for(item)} risky employee activities — ${item.name} ` +
      `information, ${level_text.value}. By severity: ` +
      `${describe_levels(item.severities, active_keys.value)}.`;

    const donut_items = computed(() =>
      items.value.map((item) => ({
        label: item.name,
        value: total_for(item),
        color: palette.value.categoryChart[item.id],
      }))
    );

    // the row opens the events behind the number currently displayed, so the
    // drill-down inherits whatever the slider is showing
    const show_events = (item?: CategoryCount) =>
      item &&
      show_gen_ai_category_events(item, {
        widget: "risk-category",
        category_id: item.id,
        direction: "employee",
        severities: active_keys.value.map((k) => LEVEL_BY_TONE[k]),
        level_text: level_text.value,
      });

    return {
      view,
      items,
      is_loading,
      level,
      headline,
      load_failed,
      LOAD_FAILED_TEXT,
      subtitle,
      total_for,
      segments_for,
      max_value,
      aria_for,
      donut_items,
      show_events,
      stops: [
        { label: "High only", pill: "High only", key: "high" },
        { label: "+ Medium", pill: "High + Medium", key: "medium" },
        { label: "All", pill: "Any Risk", key: "low" },
      ],
      T: palette,
      TY: TYPE,
      S: SIZE,
    };
  },
});
